import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody } from '@shared/components';
import { cn, formatRelativeTime } from '@shared/utils';
import type { Workspace } from '../../contracts/workspace.types';

interface WorkspaceCardProps {
  workspace: Workspace;
  className?: string;
  onEdit?: (workspace: Workspace) => void;
  onDelete?: (workspace: Workspace) => void;
}

export const WorkspaceCard = memo(function WorkspaceCard({
  workspace,
  className,
  onEdit,
  onDelete,
}: WorkspaceCardProps) {
  const statusColors: Record<string, string> = {
    active: 'status-active',
    archived: 'status-archived',
    deleted: 'status-deleted',
  };

  const visibilityIcons: Record<string, string> = {
    private: '🔒',
    team: '👥',
    public: '🌐',
  };

  return (
    <Card className={cn('workspace-card', className)}>
      <CardBody>
        <div className="workspace-card-header">
          <div
            className="workspace-card-icon"
            style={{ backgroundColor: workspace.settings?.color || '#6366f1' }}
          >
            {workspace.settings?.icon || workspace.name.charAt(0).toUpperCase()}
          </div>

          <div className="workspace-card-actions">
            {onEdit && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => onEdit(workspace)}
                aria-label="Edit workspace"
              >
                ✏️
              </button>
            )}
            {onDelete && workspace.status !== 'deleted' && (
              <button
                type="button"
                className="btn-icon btn-icon-danger"
                onClick={() => onDelete(workspace)}
                aria-label="Delete workspace"
              >
                🗑️
              </button>
            )}
          </div>
        </div>

        <Link to={`/workspaces/${workspace.id}`} className="workspace-card-link">
          <h3 className="workspace-card-title">{workspace.name}</h3>
        </Link>

        {workspace.description && (
          <p className="workspace-card-description">{workspace.description}</p>
        )}

        <div className="workspace-card-meta">
          <span className={cn('workspace-card-status', statusColors[workspace.status])}>
            {workspace.status}
          </span>

          <span className="workspace-card-visibility">
            {visibilityIcons[workspace.visibility]} {workspace.visibility}
          </span>

          <span className="workspace-card-members">
            👤 {workspace.member_count} member{workspace.member_count !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="workspace-card-footer">
          <span className="workspace-card-updated">
            Updated {formatRelativeTime(workspace.updated_at)}
          </span>
        </div>
      </CardBody>
    </Card>
  );
});
